/** @module csi/table/NewMixin */
define([
	"dojo/_base/declare",
	"csi/_base/underscore",
	"csi/action/_SortOrderMixin",
	"csi/table/NewMixinBase",
	"csi/heading/_base",
	"csi/reflection",
	"csi/xml"
], function(declare, _, _SortOrderMixin, NewMixinBase, csiHeading, reflection, xml) {

	return declare([NewMixinBase, _SortOrderMixin], {
		/** @property {Boolean} bAddToNewGroup -  boolean to allow for non-New actions to behave like New action.
		 *  When grouped, items are added in a new section. When not grouped, items are added to top of table.
		*/
		bAddToNewGroup: false,

		/** @property {boolean} forceReturnNodes - boolean switch to set ReturnNodes to true or false */
		forceReturnNodes: false,

		// QueryResult[]: group results if action is inside table groups
		//_groupResults: null,

		/** @property {string} _groupType - type of nodes contained within group where action originated */
		//_groupType: null,

		/** @property {string[]} _newNodeUrls - array of URLs created of given type */
		//_newNodeUrls: null,

		/** Not need for the new React form */
		constructor: function() {
			if (!this.useReactForm) {
				// listen for following function calls to add behavior related to stored sort order attributes
				this._connectEvent(this, "_preActionRequests", this._newActionRequests);
				this._connectEvent(this, "onSecondaryPublish", this._newSecondaryPublish);
			}
		},

		/** Not need for the new React form */
		/**
		 * Add request to get next node in view if there are more results and assigning a sort attribute value
		 */
		_newActionRequests: function(requests) {
			this._addSortOrderRequest(this.refNodeNext, requests);
		},

		/** Not need for the new React form */
		_newCreateNode: function(/*String*/url, /*String*/type, /*String?*/name, /*Number?*/flags, /*Boolean?*/bChangeIfExists) {
			// summary: track urls of nodes being created of group type
			if (type === this._groupType) {
				this._newNodeUrls = this._newNodeUrls || [];
				this._newNodeUrls.push(url);
			}
		},

		/** Not need for the new React form */
		_newCreateView: function(/*csi.view.Widget*/view) {
			// summary: assign defaults to popup forms based on grouping
			if (view.getFormNodeType && (view.getFormNodeType() == this._groupType)) {
				// if form, add grouped-by values as default values for fields
				_.each(this._groupResults, function(gr) {
					var h = gr.level.groupSpec.heading;

					if (view.defView.getField(h.m_id, h.m_path)) {
						view.addDefaultValue(h.m_id, gr.groupValue);
					}
				}, this);
			}
		},

		/** Not need for the new React form */
		_newCreateWizardView: function(/*csi.wizard.Widget*/wizard, /*Content*/page, /*csi.view.Widget*/view) {
			// summary: forward to _onCreateNewView
			this._newCreateView(view);
		},

		/** Not need for the new React form */
		/**
		 * Apply group and sort values if so configured
		 * @param {Writer} w
		 */
		_newSecondaryPublish: function(w) {
			var view = this.widget;

			// If action is initiated from action row then apply group value to new nodes using copy/paste logic of renderer to make sure valid to do so
			if (this._groupResults && !view.isNewActionsButton(this.refNode)) {
				var groupRow = view.getRowParent(this.refNode);
				var groupChild = view.getRowChildren(groupRow, true, false, true)[0];
				if (groupChild) {
					var ctxCopy = view.getQueryResultByDomNode(groupChild).getContext();

					_.each(this._newNodeUrls, function(url) {
						_.each(this._groupResults, function(gr) {
							var spec = gr.level.groupSpec;
							var h = spec.heading;
							var ctxPaste = view.getContext(url);

							csiHeading.applyHeadingRenderer(ctxCopy, h, function(renBase, hBase) {
								csiHeading.applyHeadingRenderer(ctxPaste, h, function() {
									if (renBase && renBase.canCopy(ctxCopy, h) && renBase.canPaste(ctxCopy, ctxPaste, h)) {
										w.changeNode(ctxPaste.getNodeURL()).changeAttribute(h.m_id, spec.type, gr.groupValue);
									}
								});
							});
						}, this);
					}, this);
				} else {
					console.warn("Cannot initialize group values if no other nodes in group.");
				}
			}

			// apply sort value - assign new previous value so if the action is repeated then the new nodes will follow the ones just added
			if (this._newTypeSortAttribute) {
				this._newUrlPrevious = this._writeSortOrderValues(
					_.map(this.getNewNodes(), xml.nodeGetURL),
					this._newTypeSortAttribute,
					this._newUrlPrevious,
					this._newUrlNext,
					this._newResultUrls
				);
			}
		},

		/** Not need for the new React form */
		/**
		 * Extension point that allows the ability to stop the grouping value from
		 * being applied to new nodes in the group. By default, this will always be true,
		 * but can be overridden by sub-class to prevent the group value from being applied.
		 * @param {String} urlNode - url of new node
		 * @param {String} id - id of grouping attribute
		 * @param {Object} spec - specification for grouping attribute
		 * @return {boolean} - return true if the group value should be applied, otherwise false
		 */
		shouldApplyGroupValue: function(urlNode, id, spec) {
			return true;
		},

		/** Not need for the new React form */
		_newWriterFields: function(/*String*/urlNode, /*FormData?*/fd, /*Function*/filter) {
			// summary: determine if grouping values can be applied to new nodes
			var type = this._newNodeUrls && this._newNodeUrls[urlNode];
			var rc = reflection.getClass(type);
			if (!rc) return;

			fd = fd || this.m_formData;

			this._groupResults = _.filter(this._groupResults, function(gr) {
				var spec = gr.level.groupSpec;
				var h = spec.heading;

				// C8-10673 - verify that group value should be applied
				if (!this.shouldApplyGroupValue(urlNode, h.m_id, spec)) return false;

				// type of group must match type of node being created, and must have actual grouped attribute value (i.e. ref rather than string)
				if ((type != gr.level.type) || (gr.groupValue == null)) return false;

				// attribute cannot be read-only or have expression (for now do not automatically disable expressions) or dependency
				if (!spec.ra || spec.ra.getExpression() || spec.ra.isReadOnly || h.m_dependency) return false;

				// path to attribute must be composite
				if (!rc.hasCompositePath(h.m_path)) return false;

				// if attribute already part of FormData, then value should be set to node automatically
				if (fd && fd.getField(h.m_id)) return false;

				// only assign group value if standard changeAttribute operation
				if (h.m_action && (h.m_action.hasSpecialLogic() || h.m_action.m_request)) return false;

				return true;
			}, this);
		},

		/** Not need for the new React form */
		_newPreProcess: function() {
			// summary: preprocessing for actions that could lead to new rows in the table

			// if action is within a table group, then listen for events to open popup views in order to initialize group attributes on new items
			var qr = this.widget.getQueryResultByDomNode(this.m_element);
			var ql = qr ? qr.level : this.widget.getMainQueryLevel();

			if (this.widget.isNewActionsButton(this.refNode)) {
				this._groupResults = null;
			} else if (this._groupResults = qr && qr.getGroupParents()) {
				this._groupType = ql.type;
				this._connectEvent(this, "onCreatePopup", this._newCreateView);
				this._connectEvent(this, "onWizardViewCreate", this._newCreateWizardView);
				this._connectEvent(this.m_writer, "createNode", this._newCreateNode);
				this._connectEvent(this, "toWriterFields", this._newWriterFields);
			}

			// if matrix groups are in view, then force separate request to populate new table item
			// If forceReturnNodes is true, then it should work the normal way with one request rather than forcing two requests.
			if (!this.m_action.hasReturnParameters() && !this.forceReturnNodes) {
				var paths = ql.getDependencyPaths();
				if (paths && paths.length) {
					this.m_action.setReturnParameters();
				}
			}

			return this.inherited(arguments);
		},

		_newStart: function() {
			// summary: initialize members necessary for updating table widget if adding new rows
			this.inherited(arguments);
			this.newParent = this.refNode ? this.widget.getRowParent(this.refNode) : this.widget.getRootRow();
		}
	});
});